/**
 * WebCouponInput - Coupon/Promo code input for Customer Website
 * Also includes Loyalty Phone Lookup functionality
 * Supports Light/Dark mode
 */
import { useState, useEffect } from 'react';
import { Ticket, CheckCircle, X, Loader2, Phone, User, Crown, Star, Gift } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import api from '../../utils/apiClient';

export default function WebCouponInput({
  couponCode,
  onCouponChange,
  onValidCoupon,
  onLoyaltyMemberFound,
  className = ''
}) {
  // Coupon state
  const [validating, setValidating] = useState(false);
  const [couponValid, setCouponValid] = useState(null);
  const [couponInfo, setCouponInfo] = useState(null);
  const [couponError, setCouponError] = useState('');

  // Loyalty lookup state
  const [phoneNumber, setPhoneNumber] = useState('');
  const [lookingUp, setLookingUp] = useState(false);
  const [loyaltyMember, setLoyaltyMember] = useState(null);
  const [lookupError, setLookupError] = useState('');

  // Tab state
  const [activeTab, setActiveTab] = useState('coupon'); // 'coupon' | 'loyalty'

  // Debounced coupon validation
  useEffect(() => {
    if (!couponCode || couponCode.length < 4) {
      setCouponValid(null);
      setCouponInfo(null);
      setCouponError('');
      onValidCoupon?.(null);
      return;
    }

    const timer = setTimeout(() => {
      validateCoupon(couponCode);
    }, 500);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [couponCode]);

  const validateCoupon = async (code) => {
    setValidating(true);
    setCouponError('');
    
    try {
      const res = await api.get(`/promo-codes/validate?code=${encodeURIComponent(code)}`);
      const data = res.data;
      
      if (data.valid) {
        setCouponValid(true);
        setCouponInfo({
          code: code,
          discount_percent: data.discount_percent || 10,
          message: data.message || `${data.discount_percent || 10}% discount applied!`
        });
        onValidCoupon?.({
          code: code,
          discount_percent: data.discount_percent || 10
        });
      } else {
        setCouponValid(false);
        setCouponInfo(null);
        setCouponError(data.message || 'Invalid or expired code');
        onValidCoupon?.(null);
      }
    } catch (err) {
      // Try campaign signups as fallback
      try {
        const signupRes = await api.get(`/campaign-signups/validate-code?code=${encodeURIComponent(code)}`);
        const signupData = signupRes.data;
        
        if (signupData.valid) {
          setCouponValid(true);
          setCouponInfo({
            code: code,
            discount_percent: signupData.discount_percent || 10,
            message: signupData.message || `${signupData.discount_percent || 10}% member discount!`
          });
          onValidCoupon?.({
            code: code,
            discount_percent: signupData.discount_percent || 10
          });
        } else {
          setCouponValid(false);
          setCouponError('Code not found or already used');
          onValidCoupon?.(null);
        }
      } catch (signupErr) {
        setCouponValid(false);
        setCouponError('Invalid coupon code');
        onValidCoupon?.(null);
      }
    }
    
    setValidating(false);
  };

  const clearCoupon = () => {
    onCouponChange('');
    setCouponValid(null);
    setCouponInfo(null);
    setCouponError('');
    onValidCoupon?.(null);
  };

  // Loyalty phone lookup
  const lookupLoyaltyMember = async () => {
    if (!phoneNumber || phoneNumber.length < 6) {
      setLookupError('Please enter a valid phone number');
      return;
    }

    setLookingUp(true);
    setLookupError('');
    
    try {
      const res = await api.post('/loyalty/lookup', { phone: phoneNumber });
      const data = res.data;
      
      if (data.member) {
        setLoyaltyMember(data.member);
        onLoyaltyMemberFound?.(data.member);
        
        // If member has a discount, apply it
        if (data.member.discount_percent) {
          onValidCoupon?.({
            code: `LOYALTY-${data.member.tier?.toUpperCase() || 'MEMBER'}`,
            discount_percent: data.member.discount_percent,
            from_loyalty: true
          });
        }
      } else {
        setLookupError('No loyalty account found for this number');
        setLoyaltyMember(null);
      }
    } catch (err) {
      console.error('Loyalty lookup error:', err);
      setLookupError('Member not found');
      setLoyaltyMember(null);
    }
    
    setLookingUp(false);
  };

  const clearLoyalty = () => {
    setPhoneNumber('');
    setLoyaltyMember(null);
    setLookupError('');
    onLoyaltyMemberFound?.(null);
  };

  // Get tier badge styling
  const getTierBadge = (tier) => {
    const tierStyles = {
      platinum: 'bg-purple-100 dark:bg-purple-900/40 text-purple-700 dark:text-purple-300 border-purple-300 dark:border-purple-700',
      gold: 'bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 border-amber-300 dark:border-amber-700',
      silver: 'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 border-gray-300 dark:border-gray-600',
      bronze: 'bg-orange-100 dark:bg-orange-900/40 text-orange-700 dark:text-orange-300 border-orange-300 dark:border-orange-700'
    };
    return tierStyles[tier?.toLowerCase()] || tierStyles.bronze;
  };

  return (
    <div className={`bg-card rounded-xl border border-border overflow-hidden ${className}`} data-testid="web-coupon-section">
      {/* Tabs */}
      <div className="flex border-b border-border">
        <button
          onClick={() => setActiveTab('coupon')}
          className={`flex-1 px-4 py-3 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
            activeTab === 'coupon'
              ? 'bg-primary/10 dark:bg-primary/20 text-primary border-b-2 border-primary'
              : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
          }`}
          data-testid="coupon-tab"
        >
          <Ticket className="w-4 h-4" />
          Promo Code
        </button>
        <button
          onClick={() => setActiveTab('loyalty')}
          className={`flex-1 px-4 py-3 text-sm font-medium flex items-center justify-center gap-2 transition-colors ${
            activeTab === 'loyalty'
              ? 'bg-primary/10 dark:bg-primary/20 text-primary border-b-2 border-primary'
              : 'text-muted-foreground hover:text-foreground hover:bg-muted/50'
          }`}
          data-testid="loyalty-tab"
        >
          <Crown className="w-4 h-4" />
          Loyalty Member
        </button>
      </div>

      <div className="p-4">
        {/* Coupon Tab */}
        {activeTab === 'coupon' && (
          <div>
            <p className="text-sm text-muted-foreground mb-3">
              Enter your promotional or discount code
            </p>
            
            <div className="relative">
              <Input
                type="text"
                value={couponCode}
                onChange={(e) => onCouponChange(e.target.value.toUpperCase())}
                placeholder="Enter code"
                className={`pr-12 uppercase bg-background ${
                  couponValid === true ? 'border-green-500 focus-visible:ring-green-500' :
                  couponValid === false ? 'border-red-500 focus-visible:ring-red-500' : ''
                }`}
                data-testid="web-coupon-input"
              />
              
              <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1">
                {validating && (
                  <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />
                )}
                {!validating && couponValid === true && (
                  <CheckCircle className="w-4 h-4 text-green-500" />
                )}
                {!validating && couponValid === false && couponCode.length >= 4 && (
                  <X className="w-4 h-4 text-red-500" />
                )}
                {couponCode && (
                  <button
                    onClick={clearCoupon}
                    className="p-1 hover:bg-muted rounded-full ml-1"
                    data-testid="web-coupon-clear"
                  >
                    <X className="w-3 h-3 text-muted-foreground" />
                  </button>
                )}
              </div>
            </div>
            
            {/* Valid Coupon Message */}
            {couponValid === true && couponInfo && (
              <div className="mt-3 p-3 bg-green-50 dark:bg-green-900/30 rounded-lg border border-green-200 dark:border-green-800">
                <div className="flex items-center gap-2 text-green-700 dark:text-green-300">
                  <CheckCircle className="w-4 h-4" />
                  <span className="text-sm font-medium">{couponInfo.message}</span>
                </div>
              </div>
            )}
            
            {/* Error Message */}
            {couponError && (
              <div className="mt-3 p-3 bg-red-50 dark:bg-red-900/30 rounded-lg border border-red-200 dark:border-red-800">
                <span className="text-sm text-red-700 dark:text-red-300">{couponError}</span>
              </div>
            )}
          </div>
        )}

        {/* Loyalty Tab */}
        {activeTab === 'loyalty' && (
          <div>
            {!loyaltyMember ? (
              <>
                <p className="text-sm text-muted-foreground mb-3">
                  Enter your phone number to apply loyalty benefits
                </p>
                
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      type="tel"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value.replace(/[^\d+]/g, ''))}
                      placeholder="Phone number"
                      className="pl-9 bg-background"
                      onKeyDown={(e) => e.key === 'Enter' && lookupLoyaltyMember()}
                      data-testid="web-loyalty-phone-input"
                    />
                  </div>
                  <Button
                    onClick={lookupLoyaltyMember}
                    disabled={lookingUp || phoneNumber.length < 6}
                    className="shrink-0"
                    data-testid="web-loyalty-lookup-btn"
                  >
                    {lookingUp ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Look Up'}
                  </Button>
                </div>
                
                {lookupError && (
                  <div className="mt-3 p-3 bg-amber-50 dark:bg-amber-900/30 rounded-lg border border-amber-200 dark:border-amber-800">
                    <span className="text-sm text-amber-700 dark:text-amber-300">{lookupError}</span>
                  </div>
                )}
              </>
            ) : (
              <div className="bg-gradient-to-r from-primary/5 to-primary/10 dark:from-primary/10 dark:to-primary/20 rounded-lg p-4 border border-primary/20">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center">
                      <User className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h4 className="font-medium text-foreground">{loyaltyMember.name}</h4>
                      <div className="flex items-center gap-2 mt-1">
                        <span className={`px-2 py-0.5 text-xs font-medium rounded-full border ${getTierBadge(loyaltyMember.tier)}`}>
                          {loyaltyMember.tier === 'platinum' && <Star className="w-3 h-3 inline mr-1" />}
                          {loyaltyMember.tier === 'gold' && <Crown className="w-3 h-3 inline mr-1" />}
                          {loyaltyMember.tier?.charAt(0).toUpperCase() + loyaltyMember.tier?.slice(1) || 'Member'}
                        </span>
                        {loyaltyMember.points && (
                          <span className="text-xs text-muted-foreground">
                            {loyaltyMember.points} pts
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <button
                    onClick={clearLoyalty}
                    className="p-1 hover:bg-muted rounded-full"
                  >
                    <X className="w-4 h-4 text-muted-foreground" />
                  </button>
                </div>
                
                {loyaltyMember.discount_percent > 0 && (
                  <div className="mt-3 flex items-center gap-2 text-green-700 dark:text-green-300 bg-green-50 dark:bg-green-900/30 px-3 py-2 rounded-lg">
                    <Gift className="w-4 h-4" />
                    <span className="text-sm font-medium">
                      {loyaltyMember.discount_percent}% loyalty discount applied!
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
